import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import './ModernHeader.css';
import { BackArrowIcon } from '../../../assets/Icons/SVG';
import { RED_LOGO } from '../../../shared/constants/common.js';
import ModernSearchScreen from '../../../views/Pages/searchScreen';
import useNavigation from '../../../shared/utils/useNavigation';
import { fetchUser } from '../../../shared/actions/userActions';
import { fetchCreator } from '../../../shared/actions/creatorActions';
import { isUserLoggedIn, getUserRole, getUserProfilePicture } from '../../../shared/utils/function';
import { ROLE } from '../../../shared/constants/common';
import { ROUTES } from '../../../shared/constants/routes';
import { wasSearchScreenOpen } from '../../../shared/utils/navigationState';

const ModernHeader = ({
  hide = false,
  showBackButton = false,
  dflex = 'block',
  backButtonUrl = ROUTES.DISCOVER,
  customTitle = null,
  onSearchResults = null,
  onCategorySelect = null,
  hideSearchIcon = false,
  showAvatar = true,
  user, // coming form mapStateToProps
  creator,  // coming form mapStateToProps
  fetchUser,  // coming from mapDispatchToProps
  fetchCreator,  // coming from mapDispatchToProps
  isBorder,
  history, // coming from withRouter
  showIconInRight = null,
  onShowIconInRightClick = null,
  onBackClick = null
}) => {
  const navigate = useNavigation();
  const [searchScreenOpen, setSearchScreenOpen] = useState(false);

  const isLoggedIn = isUserLoggedIn();
  const role = getUserRole();
  const isCreator = role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT;

  // Fetch user when login status changes
  useEffect(() => {
    if (isLoggedIn) {
      fetchUser();
    }
  }, [isLoggedIn, fetchUser]);

  // Fetch creator once per username — skip if login/profile already loaded creator data
  useEffect(() => {
    if (!isCreator || !user?.username) {
      return;
    }

    const loadedUsername = creator?.user_data?.username;
    if (loadedUsername === user.username) {
      return;
    }

    fetchCreator(user.username);
  }, [user?.username, isCreator, fetchCreator, creator?.user_data?.username]);

  const handleSearchToggle = () => {
    setSearchScreenOpen(true);
  };

  const handleSearchScreenClose = () => {
    setSearchScreenOpen(false);
  };

  const handleProfileClick = () => {
    if (isCreator) {
      navigate.toProfile();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearchCreatorClick = (creator) => {
    navigate.toProfile(creator.username);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Centralized back navigation handler - same as header.js
  const handleBackNavigation = useCallback(() => {
    try {
      // Check if search screen was open before navigation
      if (wasSearchScreenOpen()) {
        // Reopen search screen instead of going back
        setSearchScreenOpen(true);
        return;
      }

      // Check if there's history to go back to
      if (history && history.length > 1) {
        navigate.back();
      } else {
        // If no history, redirect to discover page as default
        navigate.toDiscover();
      }
    } catch (error) {
      // Fallback to discover page on error
      navigate.toDiscover();
    }
  }, [history, navigate]);

  const handleBack = useCallback(() => {
    if (typeof onBackClick === 'function') {
      onBackClick();
      return;
    }

    if (backButtonUrl && backButtonUrl !== ROUTES.DISCOVER) {
      navigate.to(backButtonUrl);
      return;
    }

    handleBackNavigation();
  }, [onBackClick, backButtonUrl, navigate, handleBackNavigation]);

  return (
    <>
      {!hide && (
        <>
          <header id="cm_chat_header" className={` cm_header ${customTitle ? 'cm_header_no_logo' : 'cm_header_logo_center'} ${isBorder ? 'header_bottom_border' : ''}`}>
            <div className="header-content">
              <div className={`${!showAvatar && hideSearchIcon && customTitle ? 'cm_header_left' : 'header-left'}`}>
                {showBackButton && (
                  <button
                    type="button"
                    className="modern-legal-back-btn"
                    onClick={handleBack}
                    aria-label="Go back"
                  >
                    <BackArrowIcon />
                  </button>
                )}
                <h1 className="header-title">
                  {customTitle ? (
                    <span className="custom-title">{customTitle}</span>
                  ) : (
                    <img
                      src={RED_LOGO}
                      alt="Juicy House"
                      className="header-logo"
                    />
                  )}
                </h1>
              </div>
              {/* Header Right - Hide all icons when hideSearchIcon is true */}
              {!hideSearchIcon ? (
                <div className="header-right">
                  {/* Download Icon - Show when showDownloadIcon is true */}
                  {showIconInRight && (
                    <button
                      className="right-icon-btn"
                      onClick={onShowIconInRightClick}
                    >
                      <i className={showIconInRight}></i>
                    </button>
                  )}
                  {/* Search - Only show for non-creators */}
                  {!isCreator && (
                    <div className="search-container">
                      <button
                        className="search-btn"
                        onClick={handleSearchToggle}
                      >
                        <i className="fa-solid fa-search"></i>
                      </button>
                    </div>
                  )}
                  {showAvatar && (
                    <button
                      className={`user-avatar-btn ${isCreator ? 'avatar-clickable' : 'avatar-default'}`}
                      onClick={handleProfileClick}
                    >
                      <img
                        src={getUserProfilePicture(user, creator)}
                        alt="User Profile"
                        className="cm_user_avatar"
                      />
                    </button>
                  )}
                </div>
              ) : (
                <div className="header-right">
                  {/* Download Icon - Show when showDownloadIcon is true and hideSearchIcon is true */}
                  {showIconInRight && (
                    <button
                      className="right-icon-btn"
                      onClick={onShowIconInRightClick}
                    >
                      <i className={showIconInRight}></i>
                    </button>
                  )}
                  {showAvatar && (
                    <button
                      className={`user-avatar-btn ${isCreator ? 'avatar-clickable' : 'avatar-default'}`}
                      onClick={handleProfileClick}
                    >
                      <img
                        src={getUserProfilePicture(user, creator)}
                        alt="User Profile"
                        className="cm_user_avatar"
                      />
                    </button>
                  )}
                </div>
              )}
            </div>
          </header>

          <ModernSearchScreen
            isOpen={searchScreenOpen}
            onClose={handleSearchScreenClose}
            onCreatorClick={handleSearchCreatorClick}
            onSearchResults={onSearchResults}
            onCategorySelect={onCategorySelect}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
    creator: state.creator?.creator
  };
};

const mapDispatchToProps = {
  fetchUser,
  fetchCreator
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ModernHeader)); 